import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  responsesXlsxUrl,
  surveyInvitesQuery,
  surveyQuery,
  surveyResultsQuery,
  useCreateInvites,
  useSaveQuestions,
  useSetSurveyStatus,
  type SurveyResultScope,
} from "../components/surveys/api";
import { Button } from "../components/ui/Button";
import { QuestionEditor } from "../components/surveys/QuestionEditor";
import { useQuestionDraft } from "../components/surveys/useQuestionDraft";
import { useWordingDraft } from "../components/surveys/useWordingDraft";
import { SurveyPreviewModal } from "../components/surveys/SurveyPreviewModal";
import { SurveyResultsPanel } from "../components/surveys/SurveyResultsPanel";
import { SurveyResponsesPanel } from "../components/surveys/SurveyResponsesPanel";
import { SmsDryRunPanel } from "../components/surveys/SmsDryRunPanel";
import { SmsTemplateCard } from "../components/surveys/SmsTemplateCard";
import { SurveyWordingCard } from "../components/surveys/SurveyWordingCard";
import { InviteRecipientsCard } from "../components/surveys/InviteRecipientsCard";
import { Pill } from "../components/ui/Pill";
import { useToast } from "../components/ui/toastContext";
import styles from "../components/surveys/SurveyDetail.module.css";

export const Route = createFileRoute("/_auth/_messaging/surveys/$id")({
  staticData: { titleKey: "nav.messaging", breadcrumbKey: "breadcrumb.operate", breadcrumbN: 7 },
  component: SurveyDetail,
});

function SurveyDetail() {
  const { id } = Route.useParams();
  const { t } = useTranslation();
  const { show } = useToast();
  const [scope, setScope] = useState<SurveyResultScope>("live");
  const { data: survey, isLoading } = useQuery(surveyQuery(id));
  const { data: results } = useQuery(surveyResultsQuery(id, scope));
  const { data: invites } = useQuery(surveyInvitesQuery(id));
  const saveQuestions = useSaveQuestions();
  const setStatus = useSetSurveyStatus();
  const createInvites = useCreateInvites();

  const [draft, setDraft] = useQuestionDraft(survey);
  const [wording, setWording] = useWordingDraft(survey);
  const [previewing, setPreviewing] = useState(false);

  if (isLoading || !survey) return <span style={{ color: "var(--fg-mute)" }}>…</span>;

  const isDraft = survey.status === "draft";

  function onSaveQuestions() {
    saveQuestions.mutate(
      { id, body: { questions: draft } },
      {
        onSuccess: () => show({ tone: "success", message: t("survey.questionsSaved") }),
        onError: (e) => show({ tone: "danger", message: String(e) }),
      },
    );
  }

  function move(status: "draft" | "open" | "closed") {
    setStatus.mutate(
      { id, body: { status } },
      {
        onSuccess: () => show({ tone: "success", message: t(`survey.moved.${status}`) }),
        onError: () => show({ tone: "danger", message: t("survey.moveFailed") }),
      },
    );
  }

  return (
    <div className={styles.wrap}>
      <header className={styles.head}>
        <h2 className={styles.title}>{survey.title}</h2>
        <Pill tone={survey.status === "open" ? "success" : "default"}>
          {t(`survey.status.${survey.status}`)}
        </Pill>
        <div className={styles.actions}>
          {/* Before anyone is sent a link, and from the drafts rather than the
              saved copy — see `toPreview`. */}
          <Button onClick={() => setPreviewing(true)}>{t("survey.preview.open")}</Button>
          {isDraft && (
            <Button tone="primary" variant="solid" onClick={() => move("open")}>
              {t("survey.open")}
            </Button>
          )}
          {survey.status === "open" && (
            <Button onClick={() => move("closed")}>{t("survey.close")}</Button>
          )}
        </div>
      </header>

      {previewing && (
        <SurveyPreviewModal
          wording={wording}
          questions={draft}
          onClose={() => setPreviewing(false)}
        />
      )}

      <SurveyWordingCard survey={survey} form={wording} setForm={setWording} />

      <QuestionEditor
        questions={draft}
        editable={isDraft}
        onChange={setDraft}
        onSave={onSaveQuestions}
        saving={saveQuestions.isPending}
      />

      <InviteRecipientsCard
        surveyId={id}
        status={survey.status}
        invites={invites?.items ?? []}
        total={invites?.total ?? 0}
        onCreate={(body) =>
          createInvites.mutate(
            { id, body },
            {
              onSuccess: (b) =>
                show({
                  tone: "success",
                  message: t("survey.invitesCreated", {
                    created: b.created,
                    skipped: b.skipped_existing + b.skipped_no_phone,
                  }),
                }),
              onError: () => show({ tone: "danger", message: t("survey.inviteFailed") }),
            },
          )
        }
        pending={createInvites.isPending}
      />

      <SmsTemplateCard surveyId={id} template={survey.sms_template ?? ""} />

      <SmsDryRunPanel surveyId={id} />

      {results && (
        <SurveyResultsPanel
          results={results}
          xlsxUrl={responsesXlsxUrl(id, scope)}
          scope={scope}
          onScope={setScope}
        />
      )}

      {/* Named answers under the aggregates, on the same scope: the figures say
          how it went, this says who to ring. */}
      <SurveyResponsesPanel surveyId={id} questions={survey.questions ?? []} scope={scope} />
    </div>
  );
}
